import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../../context/AuthContext";
import { useBarOwnerData } from "../../../../context/BarOwnerDataContext";
import { auth } from "../../../../config/firebase";
import {
  reauthenticateWithCredential,
  EmailAuthProvider,
  updatePassword,
} from "firebase/auth";

const ProfileSettings = () => {
  const { currentUser, setCurrentUser } = useAuth();
  const { fetchProfile, getProfile, invalidateCache } = useBarOwnerData();
  const navigate = useNavigate();
  const { user, loading, error: cacheError } = getProfile();

  const [userData, setUserData] = useState({
    name: "",
    email: "",
    joinDate: "",
    lastLogin: "",
  });
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Name editing
  const [isEditingName, setIsEditingName] = useState(false);
  const [nameSaving, setNameSaving] = useState(false);
  const [editedName, setEditedName] = useState("");

  // Password Management & Danger Zone state
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
    deletePassword: "",
  });
  const [securityLoading, setSecurityLoading] = useState(false);

  // Fetch user data when cache is empty
  useEffect(() => {
    const cached = getProfile();
    if (!cached.user && !cached.loading) {
      fetchProfile();
    }
  }, [fetchProfile, getProfile]);

  // Update userData when cached user is available
  useEffect(() => {
    if (user) {
      setUserData({
        name: user.name,
        email: user.email,
        joinDate: new Date(user.created_at).toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
        }),
        lastLogin: user.last_login
          ? new Date(user.last_login).toLocaleDateString("en-US", {
              year: "numeric",
              month: "long",
              day: "numeric",
              hour: "numeric",
              minute: "numeric",
            })
          : "Never",
      });
      setError(null);
    }
  }, [user]);

  useEffect(() => {
    if (cacheError) {
      setError(cacheError);
    } else if (user) {
      setError(null);
    }
  }, [cacheError, user]);

  const handleNameChange = (e) => {
    setEditedName(e.target.value);
  };

  const handleStartEditName = () => {
    setEditedName(userData.name);
    setIsEditingName(true);
  };

  const handleCancelEditName = () => {
    setEditedName("");
    setIsEditingName(false);
  };

  const handleSaveName = async () => {
    const trimmed = editedName?.trim() || "";
    if (!trimmed) {
      setError("Name is required");
      return;
    }
    try {
      setError(null);
      setSuccess(null);
      setNameSaving(true);
      const response = await fetch("/api/users/profile", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ name: trimmed }),
      });
      const data = await response.json();
      if (data.success && data.user) {
        setUserData((prev) => ({ ...prev, name: data.user.name }));
        setSuccess("Name updated successfully");
        setIsEditingName(false);
        setEditedName("");
        invalidateCache("profile");
        fetchProfile();
      } else {
        setError(data.error || "Failed to update name");
      }
    } catch (err) {
      setError("Failed to update name");
      console.error("Update name error:", err);
    } finally {
      setNameSaving(false);
    }
  };

  const handlePasswordChange = (e) => {
    setPasswordData({
      ...passwordData,
      [e.target.name]: e.target.value,
    });
    setError(null);
    setSuccess(null);
  };

  const handlePasswordReset = async () => {
    try {
      setError(null);
      setSuccess(null);

      if (
        !passwordData.currentPassword ||
        !passwordData.newPassword ||
        !passwordData.confirmPassword
      ) {
        setError("All fields are required");
        return;
      }

      if (passwordData.newPassword !== passwordData.confirmPassword) {
        setError("New passwords do not match");
        return;
      }

      if (passwordData.newPassword.length < 6) {
        setError("Password must be at least 6 characters long");
        return;
      }

      setSecurityLoading(true);

      const user = auth.currentUser;
      if (!user || !user.email) {
        setError("User not authenticated");
        setSecurityLoading(false);
        return;
      }

      const credential = EmailAuthProvider.credential(
        user.email,
        passwordData.currentPassword
      );
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, passwordData.newPassword);

      setSuccess("Password updated successfully");
      setShowPasswordForm(false);
      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
        deletePassword: passwordData.deletePassword,
      });
    } catch (err) {
      console.error("Password update error:", err);
      if (err.code === "auth/wrong-password") {
        setError("Current password is incorrect");
      } else if (err.code === "auth/weak-password") {
        setError("New password is too weak");
      } else {
        setError(err.message || "Failed to update password");
      }
    } finally {
      setSecurityLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    try {
      setError(null);

      if (!passwordData.deletePassword) {
        setError("Please enter your password to confirm account deletion");
        return;
      }

      setSecurityLoading(true);

      const user = auth.currentUser;
      if (!user || !user.email) {
        setError("User not authenticated");
        setSecurityLoading(false);
        return;
      }

      const credential = EmailAuthProvider.credential(
        user.email,
        passwordData.deletePassword
      );
      await reauthenticateWithCredential(user, credential);

      const response = await fetch("/api/settings/delete-account", {
        method: "POST",
        credentials: "include",
      });
      const data = await response.json();

      if (data.success) {
        setCurrentUser(null);
        await auth.signOut();
        navigate("/");
      } else {
        setError(data.error || "Failed to delete account");
        setSecurityLoading(false);
      }
    } catch (err) {
      console.error("Delete account error:", err);
      if (err.code === "auth/wrong-password") {
        setError("Password is incorrect");
      } else {
        setError(err.message || "Failed to delete account");
      }
      setSecurityLoading(false);
    }
  };

  const handleShowDeleteConfirm = () => {
    setShowPasswordConfirm(true);
  };

  if (loading) {
    return (
      <div className="settings-content">
        <div className="loading-container">
          <div className="loading-spinner">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-content">
      <div className="settings-section">
        <div className="section-header">
          <h2 className="section-title">Profile</h2>
          <p className="section-subtitle">Account information and security</p>
        </div>

        {error && (
          <div
            className="alert alert-error"
            style={{
              padding: "10px",
              marginBottom: "20px",
              backgroundColor: "#fee",
              color: "#c33",
              borderRadius: "5px",
            }}
          >
            {error}
          </div>
        )}
        {success && (
          <div
            className="alert alert-success"
            style={{
              padding: "10px",
              marginBottom: "20px",
              backgroundColor: "#efe",
              color: "#3c3",
              borderRadius: "5px",
            }}
          >
            {success}
          </div>
        )}

        <div className="settings-card">
          <div className="card-header">
            <h3>Account Information</h3>
            {!isEditingName && (userData.name || userData.email) && (
              <button
                type="button"
                className="edit-btn"
                onClick={handleStartEditName}
                disabled={nameSaving}
              >
                Edit
              </button>
            )}
          </div>

          <div className="info-grid">
            <div className="info-item">
              <span className="info-label">Name: </span>
              {isEditingName ? (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                  }}
                >
                  <input
                    type="text"
                    value={editedName}
                    onChange={handleNameChange}
                    className="settings-input"
                    placeholder="Your name"
                    disabled={nameSaving}
                    maxLength={128}
                  />
                  <div style={{ display: "flex", gap: "8px" }}>
                    <button
                      type="button"
                      className="save-btn"
                      onClick={handleSaveName}
                      disabled={nameSaving || !editedName?.trim()}
                    >
                      {nameSaving ? "Saving..." : "Save"}
                    </button>
                    <button
                      type="button"
                      className="cancel-btn"
                      onClick={handleCancelEditName}
                      disabled={nameSaving}
                      style={{
                        padding: "8px 16px",
                        backgroundColor: "#f5f5f5",
                        border: "none",
                        borderRadius: "5px",
                        cursor: "pointer",
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <span className="info-value">{userData.name || "—"}</span>
              )}
            </div>
            <div className="info-item">
              <span className="info-label">Email Address: </span>
              <span className="info-value">{userData.email}</span>
              <p style={{ fontSize: "12px", color: "#666", marginTop: "5px" }}>
                Email cannot be changed for security reasons
              </p>
            </div>
            <div className="info-item">
              <span className="info-label">Member Since: </span>
              <span className="info-value">{userData.joinDate}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Last Login: </span>
              <span className="info-value">{userData.lastLogin}</span>
            </div>
          </div>
        </div>

        <div className="settings-card">
          <div className="card-header">
            <h3>Password Management</h3>
            <button
              className="action-btn"
              onClick={() => setShowPasswordForm(!showPasswordForm)}
              disabled={securityLoading}
            >
              {showPasswordForm ? "Cancel" : "Change Password"}
            </button>
          </div>

          {showPasswordForm ? (
            <div className="password-form">
              <div className="form-grid">
                <div className="input-group">
                  <label className="input-label">Current Password</label>
                  <input
                    type="password"
                    name="currentPassword"
                    value={passwordData.currentPassword}
                    onChange={handlePasswordChange}
                    className="settings-input"
                    placeholder="Enter current password"
                    disabled={securityLoading}
                  />
                </div>

                <div className="input-group">
                  <label className="input-label">New Password</label>
                  <input
                    type="password"
                    name="newPassword"
                    value={passwordData.newPassword}
                    onChange={handlePasswordChange}
                    className="settings-input"
                    placeholder="Enter new password"
                    disabled={securityLoading}
                  />
                </div>

                <div className="input-group">
                  <label className="input-label">Confirm New Password</label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={passwordData.confirmPassword}
                    onChange={handlePasswordChange}
                    className="settings-input"
                    placeholder="Confirm new password"
                    disabled={securityLoading}
                  />
                </div>
              </div>

              <div className="form-actions">
                <button
                  className="save-btn"
                  onClick={handlePasswordReset}
                  disabled={securityLoading}
                >
                  {securityLoading ? "Updating..." : "Update Password"}
                </button>
              </div>
            </div>
          ) : (
            <div className="password-info">
              <p>Change your password to keep your account secure.</p>
              <p className="text-muted">
                For security reasons, we recommend changing your password
                regularly.
              </p>
            </div>
          )}
        </div>

        <div className="settings-card danger-card">
          <div className="card-header">
            <h3>Danger Zone</h3>
          </div>

          <div className="danger-content">
            <div className="danger-item">
              <div className="danger-info">
                <h4>Delete Account</h4>
                <p>
                  Permanently delete your account and all associated data. This
                  includes:
                </p>
                <ul style={{ marginLeft: "20px", marginTop: "10px" }}>
                  <li>All your personal information</li>
                  <li>All bars you own and all their data</li>
                  <li>All shows and bookings</li>
                  <li>All financial records</li>
                  <li>All conversations and messages</li>
                  <li>All notifications</li>
                </ul>
                <p
                  style={{
                    color: "#c33",
                    fontWeight: "bold",
                    marginTop: "10px",
                  }}
                >
                  This action CANNOT be undone.
                </p>
              </div>
              <button
                className="danger-btn"
                onClick={handleShowDeleteConfirm}
                disabled={securityLoading}
              >
                Delete Account
              </button>
            </div>
          </div>
        </div>

        {showPasswordConfirm && (
          <div
            className="modal-overlay"
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0,0,0,0.7)",
              zIndex: 1000,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <div
              className="modal-container"
              style={{
                backgroundColor: "white",
                borderRadius: "8px",
                padding: "30px",
                maxWidth: "500px",
                width: "90%",
              }}
            >
              <div className="modal-header" style={{ marginBottom: "20px" }}>
                <h3
                  className="modal-title"
                  style={{
                    fontSize: "24px",
                    fontWeight: "bold",
                    color: "#c33",
                  }}
                >
                  ⚠️ Confirm Account Deletion
                </h3>
              </div>
              <div className="modal-content">
                <p
                  style={{
                    marginBottom: "20px",
                    fontWeight: "bold",
                    fontSize: "18px",
                  }}
                >
                  This will permanently delete your account and ALL associated
                  data.
                </p>
                <p style={{ marginBottom: "15px" }}>
                  To confirm, please enter your password:
                </p>
                <div className="input-group">
                  <label className="input-label">Password</label>
                  <input
                    type="password"
                    name="deletePassword"
                    value={passwordData.deletePassword}
                    onChange={handlePasswordChange}
                    className="settings-input"
                    placeholder="Enter your password"
                    disabled={securityLoading}
                    style={{ width: "100%" }}
                  />
                </div>
                <p
                  style={{ color: "#666", fontSize: "12px", marginTop: "10px" }}
                >
                  This is your FINAL confirmation. The deletion cannot be
                  undone.
                </p>
                <div
                  className="modal-actions"
                  style={{ display: "flex", gap: "10px", marginTop: "25px" }}
                >
                  <button
                    className="cancel-btn"
                    onClick={() => {
                      setShowPasswordConfirm(false);
                      setPasswordData({ ...passwordData, deletePassword: "" });
                    }}
                    disabled={securityLoading}
                    style={{
                      flex: 1,
                      padding: "10px",
                      backgroundColor: "#f5f5f5",
                      border: "none",
                      borderRadius: "5px",
                      cursor: "pointer",
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    className="danger-btn"
                    onClick={() => {
                      setShowPasswordConfirm(false);
                      setShowDeleteConfirm(true);
                    }}
                    disabled={securityLoading || !passwordData.deletePassword}
                    style={{
                      flex: 1,
                      padding: "10px",
                      backgroundColor: "#c33",
                      color: "white",
                      border: "none",
                      borderRadius: "5px",
                      cursor: "pointer",
                      opacity:
                        securityLoading || !passwordData.deletePassword
                          ? 0.5
                          : 1,
                    }}
                  >
                    Continue to Final Confirmation
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showDeleteConfirm && (
          <div
            className="modal-overlay"
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0,0,0,0.7)",
              zIndex: 1001,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <div
              className="modal-container"
              style={{
                backgroundColor: "white",
                borderRadius: "8px",
                padding: "30px",
                maxWidth: "500px",
                width: "90%",
              }}
            >
              <div className="modal-header" style={{ marginBottom: "20px" }}>
                <h3
                  className="modal-title"
                  style={{
                    fontSize: "24px",
                    fontWeight: "bold",
                    color: "#c33",
                  }}
                >
                  🗑️ Final Confirmation
                </h3>
              </div>
              <div className="modal-content">
                <p
                  style={{
                    marginBottom: "20px",
                    fontWeight: "bold",
                    fontSize: "18px",
                  }}
                >
                  Are you absolutely sure?
                </p>
                <p style={{ marginBottom: "15px", fontSize: "16px" }}>
                  Your account will be permanently deleted immediately,
                  including:
                </p>
                <ul style={{ marginLeft: "20px", marginBottom: "20px" }}>
                  <li>All bars and bar data</li>
                  <li>All shows and bookings</li>
                  <li>All financial records</li>
                  <li>All messages and notifications</li>
                  <li>All user data</li>
                </ul>
                <p style={{ color: "#c33", fontWeight: "bold" }}>
                  THERE IS NO UNDO FOR THIS ACTION.
                </p>
                <div
                  className="modal-actions"
                  style={{ display: "flex", gap: "10px", marginTop: "25px" }}
                >
                  <button
                    className="cancel-btn"
                    onClick={() => {
                      setShowDeleteConfirm(false);
                      setPasswordData({ ...passwordData, deletePassword: "" });
                    }}
                    disabled={securityLoading}
                    style={{
                      flex: 1,
                      padding: "10px",
                      backgroundColor: "#f5f5f5",
                      border: "none",
                      borderRadius: "5px",
                      cursor: "pointer",
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    className="danger-btn"
                    onClick={handleDeleteAccount}
                    disabled={securityLoading}
                    style={{
                      flex: 1,
                      padding: "10px",
                      backgroundColor: "#c33",
                      color: "white",
                      border: "none",
                      borderRadius: "5px",
                      cursor: "pointer",
                      opacity: securityLoading ? 0.5 : 1,
                    }}
                  >
                    {securityLoading
                      ? "Deleting..."
                      : "⚠️ Yes, Delete My Account Now"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfileSettings;
